import { Box, Typography } from "@mui/material";
import { useMemo } from "react";
import {
  ResponsiveContainer,
  AreaChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Area,
  ReferenceLine,
  ReferenceArea,
  ReferenceDot,
} from "recharts";
import { IBenchmarks } from "../../types/insights";

const benchmarkMap = {
  "0-25": {
    median: 12.5,
    rangeStart: 0,
    rangeEnd: 25,
  },
  "25-50": {
    median: 37.5,
    rangeStart: 25,
    rangeEnd: 50,
  },
  "50-75": {
    median: 62.5,
    rangeStart: 50,
    rangeEnd: 75,
  },
  "75-100": {
    median: 87.5,
    rangeStart: 75,
    rangeEnd: 100,
  },
  Unknown: {
    median: 0,
    rangeStart: 0,
    rangeEnd: 0,
  },
};

type BenchmarkingProps = {
  benchmarks: IBenchmarks;
};

export const Benchmarking = ({
  benchmarks,
}: BenchmarkingProps): React.ReactElement => {
  return (
    <Box
      sx={{
        margin: 3,
      }}
      position="relative"
    >
      <Box mt={1}>
        <Typography variant="h5">
          You’re ranked in the:
          <br />
          <strong>
            {benchmarkMap[benchmarks.overallBracket].rangeStart}-
            {benchmarkMap[benchmarks.overallBracket].rangeEnd}th percentile
          </strong>
        </Typography>
      </Box>

      <BellCurveChart
        percentile={benchmarkMap[benchmarks.overallBracket].median}
        rangeStart={benchmarkMap[benchmarks.overallBracket].rangeStart}
        rangeEnd={benchmarkMap[benchmarks.overallBracket].rangeEnd}
      />
    </Box>
  );
};

// Generate a normalized bell curve dataset (peak y=1)
function generateNormalizedBellCurve(
  mean: number,
  sd: number,
  points: number,
): Array<{ x: number; y: number }> {
  const data: Array<{ x: number; y: number }> = [];
  const step = (6 * sd) / (points - 1);
  for (let i = 0; i < points; i++) {
    const x = mean - 3 * sd + step * i;
    const y = Math.exp(-((x - mean) ** 2) / (2 * sd ** 2));
    data.push({ x: Number(x.toFixed(2)), y: Number(y.toFixed(4)) });
  }
  return data;
}

type ChartProps = {
  percentile: number;
  rangeStart: number;
  rangeEnd: number;
  mean?: number;
  sd?: number;
};

export const BellCurveChart: React.FC<ChartProps> = ({
  percentile,
  rangeStart,
  rangeEnd,
  mean = 0,
  sd = 1,
}) => {
  const data = useMemo(
    () => generateNormalizedBellCurve(mean, sd, 100),
    [mean, sd],
  );
  const idx = Math.round((percentile / 100) * (data.length - 1));
  const dot = data[idx] || { x: mean, y: 0 };

  // Highlight segment from 20th to 45th percentile
  const startIdx = Math.round((rangeStart / 100) * (data.length - 1));
  const endIdx = Math.round((rangeEnd / 100) * (data.length - 1));
  const highlightStart = data[startIdx].x;
  const highlightEnd = data[endIdx].x;

  return (
    <Box position="relative" width="100%" height={200}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={data}
          margin={{ top: 10, right: 10, left: 10, bottom: 10 }}
        >
          {/* Vertical dotted grid */}
          <CartesianGrid
            vertical={true}
            horizontal={false}
            stroke="#e0e0e0"
            strokeDasharray="3 3"
          />
          {/* X-Axis */}
          <XAxis
            dataKey="x"
            type="number"
            domain={[data[0].x, data[data.length - 1].x]}
            tick={false}
            axisLine={false}
          />
          {/* Y-Axis hidden */}
          <YAxis hide domain={[0, 1]} />
          {/* <Tooltip cursor={false} /> */}
          {/* Area fill */}
          <Area
            type="monotone"
            dataKey="y"
            stroke="#d0d0d0"
            strokeWidth={1.5}
            fill="#fffaef"
            dot={false}
          />
          {/* Overlay line */}

          {/* Median line */}
          <ReferenceLine x={mean} stroke="#aaa" strokeDasharray="4 4" />

          {/* Highlight area segment */}
          <ReferenceArea
            x1={highlightStart}
            x2={highlightEnd}
            fill="#fde5b5"
            fillOpacity={0.4}
          />

          {/* Percentile dot */}
          <ReferenceDot
            x={dot.x}
            y={dot.y}
            r={4}
            fill="white"
            stroke="#faaa0a"
            strokeWidth={2.5}
          />
        </AreaChart>
      </ResponsiveContainer>

      {/* Percentile label overlay */}
      {/* <Box
        sx={{
          position: 'absolute',
          top: `calc(${(1 - dot.y) * 100}% + 8px)`,
          left: `calc(${percentile}% - 8px)`,
          transform: 'translate(-50%, -50%)'
        }}
      >
        <Typography variant="h5">{percentile}</Typography>
      </Box> */}
    </Box>
  );
};
